/**
 * The Materials tab's undrawn sheets — **Add a material** (the toolbar,
 * Board 2) and a line's ⋯ *Edit · Spread over days · Move to another visit ·
 * Remove* (§8.3; the ⋯'s words are the contract's, `contractText`).
 *
 * No board draws these sheets open, so each is built from the boards' own
 * words — *Material*, *Find a material*, *Quantity*, *Comes from* and its five
 * sources, *Vendor*, *Needed*, *Day n*, *Why*, *Cancel*, *Save* — with the
 * menu's words as titles and buttons (lead decisions, round 2; each a
 * DEVIATION REQUEST in the report). Every one is Slice 6's centred popup, and
 * Board 16's sheet from the bottom on a phone.
 *
 * Nothing here adds up quantities: *Spread over days* sends what is typed per
 * day, and the server says whether it is the line's whole quantity.
 */
import { useEffect, useRef, useState } from 'react'
import type {
  MaterialCatalog, MaterialLine, MaterialLineDetail, MaterialSource, VendorRef, VisitRead,
} from '@rowted/shared'
import { MATERIAL_SOURCES } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { request } from '../lib/api.js'
import { visitPath, useVisitWrite, type VisitWriteResult } from './api.js'
import { VisitButton, VisitPopup, VisitTile } from './sheet.js'
import { VisitGlyph } from './glyphs.js'
import { VisitMark } from './visit-mark.js'
import { COMMON, MATERIALS, ORDER } from './copy.js'
import { dayText } from './format.js'
import {
  unitWord,
} from './materials-parts.js'
import { ChoiceChip, FIELD_BOX, FIELD_INPUT, FieldLabel, RadioDot } from './fields.js'

interface SheetProps {
  read: VisitRead
  phone: boolean
  onClose: () => void
  onDone: (result: VisitWriteResult) => void
}

function useSubmit(read: VisitRead, onDone: (result: VisitWriteResult) => void, onClose: () => void) {
  const write = useVisitWrite(read.id)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  async function submit(method: 'POST' | 'PATCH', path: string, body: unknown) {
    setBusy(true)
    setError(null)
    try {
      const result = await write(method, path, body, { tab: 'materials' })
      onDone(result)
      onClose()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }
  return { busy, error, submit }
}

const lineTile = <VisitTile glyph="material" tone="grey" size={34} />

/** The catalog and the vendors, for the words typed — `GET …/materials/catalog?q=`. */
function useCatalog(read: VisitRead, q: string): MaterialCatalog | null {
  const [found, setFound] = useState<MaterialCatalog | null>(null)
  const latest = useRef(0)
  useEffect(() => {
    const ticket = ++latest.current
    const timer = window.setTimeout(() => {
      request<MaterialCatalog>(`${visitPath(read.id, '/materials/catalog')}?q=${encodeURIComponent(q.trim())}`)
        .then((body) => { if (ticket === latest.current) setFound(body) })
        .catch(() => { /* the lists stay as they were */ })
    }, 150)
    return () => window.clearTimeout(timer)
  }, [q, read.id])
  return found
}

function SourceChips({ value, onChange }: { value: MaterialSource; onChange: (source: MaterialSource) => void }) {
  return (
    <div role="radiogroup" aria-label={MATERIALS.head.comesFrom} className="flex flex-wrap gap-1.5">
      {MATERIAL_SOURCES.map((source) => (
        <ChoiceChip key={source} size={28} on={value === source} onClick={() => onChange(source)}>
          {MATERIALS.source[source]}
        </ChoiceChip>
      ))}
    </div>
  )
}

function VendorList({ vendors, value, onChange }: {
  vendors: VendorRef[]
  value: string | null
  onChange: (vendorId: string) => void
}) {
  return (
    <div role="radiogroup" aria-label={ORDER.vendor} className="max-h-44 overflow-y-auto rounded-[10px] border border-line">
      {vendors.map((vendor) => {
        const on = vendor.id === value
        return (
          <button
            key={vendor.id}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(vendor.id)}
            className={cn('flex min-h-11 w-full items-center gap-2.5 border-t border-line-faint px-3 py-2 text-left first:border-t-0',
              on ? 'bg-green-band-bg' : 'bg-surface hover:bg-hover')}
          >
            <RadioDot on={on} size={16} />
            <span className="flex min-w-0 flex-col">
              <b className="truncate text-[12.5px]">{vendor.name}</b>
              {vendor.city && <span className="truncate text-[11.5px] text-text-muted">{vendor.city}</span>}
            </span>
          </button>
        )
      })}
    </div>
  )
}

function QtyField({ id, value, onChange, unit }: { id: string; value: string; onChange: (value: string) => void; unit: string }) {
  return (
    <div className={FIELD_BOX}>
      <input id={id} inputMode="decimal" value={value} onChange={(event) => onChange(event.target.value)} className={FIELD_INPUT} />
      {unit && <span className="ml-1.5 text-[12px] text-text-faint">{unit}</span>}
    </div>
  )
}

function Problem({ error }: { error: string | null }) {
  return error ? <p className="m-0 text-[12.5px] text-danger">{error}</p> : null
}

// ---------------------------------------------------------------------------
// Add a material
// ---------------------------------------------------------------------------

export function AddMaterialSheet({ read, phone, onClose, onDone }: SheetProps) {
  const [q, setQ] = useState('')
  const catalog = useCatalog(read, q)
  const [item, setItem] = useState<MaterialCatalog['items'][number] | null>(null)
  const [qty, setQty] = useState('')
  const [source, setSource] = useState<MaterialSource>('yard')
  const [vendorId, setVendorId] = useState<string | null>(null)
  const days = read.overview.days.days.map((tile) => tile.day)
  const [day, setDay] = useState<number | null>(days.find((dayRef) => dayRef.relative === 'tomorrow')?.index ?? days[0]?.index ?? null)
  const { busy, error, submit } = useSubmit(read, onDone, onClose)
  const amount = Number(qty)
  const ready = item !== null && amount > 0 && day !== null && (source === 'yard' || vendorId !== null)

  return (
    <VisitPopup
      width={470}
      phone={phone}
      title={MATERIALS.add}
      sub={read.reference}
      mark={lineTile}
      onClose={onClose}
      footer={(
        <>
          <VisitButton size={phone ? 'phone' : 'popup'} onClick={onClose}>{COMMON.cancel}</VisitButton>
          <VisitButton
            size={phone ? 'phone' : 'popup'}
            tone="primary"
            glyph="plus"
            disabled={busy || !ready}
            onClick={() => void submit('POST', '/materials', {
              itemId: item?.item.id, qty: amount, source, ...(source === 'yard' ? {} : { vendorId }), days: [{ day, qty: amount }],
            })}
          >
            {MATERIALS.add}
          </VisitButton>
        </>
      )}
    >
      <div className="flex flex-col gap-3.5">
        <div className="min-w-0">
          <FieldLabel required>{MATERIALS.head.material}</FieldLabel>
          {item ? (
            <button type="button" onClick={() => setItem(null)} className={cn(FIELD_BOX, 'gap-2 text-left')}>
              <span className="inline-flex text-text-muted"><VisitGlyph name="material" size={15} stroke={2} /></span>
              <b className="min-w-0 flex-1 truncate font-semibold">{item.item.name}</b>
            </button>
          ) : (
            <>
              <div className={FIELD_BOX}>
                <span className="mr-2 inline-flex text-text-faint"><VisitGlyph name="search" size={15} stroke={2} /></span>
                <input
                  autoFocus
                  value={q}
                  onChange={(event) => setQ(event.target.value)}
                  placeholder={MATERIALS.find}
                  aria-label={MATERIALS.find}
                  className={FIELD_INPUT}
                />
              </div>
              {catalog && catalog.items.length > 0 && (
                <div className="mt-1.5 max-h-48 overflow-y-auto rounded-[10px] border border-line">
                  {catalog.items.map((row) => (
                    <button
                      key={row.item.id}
                      type="button"
                      onClick={() => setItem(row)}
                      className="flex min-h-11 w-full items-center gap-2 border-t border-line-faint px-3 py-2 text-left first:border-t-0 hover:bg-hover"
                    >
                      <b className="min-w-0 flex-1 truncate text-[12.5px]">{row.item.name}</b>
                      {row.unit && <span className="text-[11.5px] text-text-muted">{row.unit.one}</span>}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="min-w-0">
          <FieldLabel htmlFor="add-qty" required>{ORDER.quantity}</FieldLabel>
          <QtyField id="add-qty" value={qty} onChange={setQty} unit={item?.unit ? unitWord(amount, item.unit) : ''} />
        </div>

        <div className="min-w-0">
          <FieldLabel required>{MATERIALS.head.comesFrom}</FieldLabel>
          <SourceChips value={source} onChange={setSource} />
        </div>

        {source !== 'yard' && catalog && (
          <div className="min-w-0">
            <FieldLabel required>{ORDER.vendor}</FieldLabel>
            <VendorList vendors={catalog.vendors} value={vendorId} onChange={setVendorId} />
          </div>
        )}

        <div className="min-w-0">
          <FieldLabel required>{COMMON.needed}</FieldLabel>
          <div role="radiogroup" aria-label={COMMON.needed} className="flex flex-wrap gap-1.5">
            {days.map((dayRef) => (
              <ChoiceChip key={dayRef.index} size={28} on={day === dayRef.index} onClick={() => setDay(dayRef.index)}>
                {COMMON.dayDate(String(dayRef.index), dayText(dayRef.date, 'day'))}
              </ChoiceChip>
            ))}
          </div>
        </div>
        <Problem error={error} />
      </div>
    </VisitPopup>
  )
}

// ---------------------------------------------------------------------------
// ⋯ Edit — the quantity, where it comes from, its vendor
// ---------------------------------------------------------------------------

export function EditLineSheet({ read, line, phone, onClose, onDone }: SheetProps & { line: MaterialLine }) {
  const [qty, setQty] = useState(String(line.qty))
  const [source, setSource] = useState<MaterialSource>(line.source)
  const [vendorId, setVendorId] = useState<string | null>(line.vendor?.id ?? null)
  const catalog = useCatalog(read, '')
  const { busy, error, submit } = useSubmit(read, onDone, onClose)
  const amount = Number(qty)

  return (
    <VisitPopup
      width={470}
      phone={phone}
      title={line.names.name}
      sub={MATERIALS.menu.edit}
      mark={lineTile}
      onClose={onClose}
      footer={(
        <>
          <VisitButton size={phone ? 'phone' : 'popup'} onClick={onClose}>{COMMON.cancel}</VisitButton>
          <VisitButton
            size={phone ? 'phone' : 'popup'}
            tone="primary"
            glyph="check"
            disabled={busy || !(amount > 0) || (source !== 'yard' && !vendorId)}
            onClick={() => void submit('PATCH', `/materials/${encodeURIComponent(line.id)}`, {
              version: line.version,
              ...(amount !== line.qty ? { qty: amount } : {}),
              ...(source !== line.source ? { source } : {}),
              ...(source === 'yard' ? (line.vendor ? { vendorId: null } : {}) : vendorId !== (line.vendor?.id ?? null) ? { vendorId } : {}),
            })}
          >
            {COMMON.save}
          </VisitButton>
        </>
      )}
    >
      <div className="flex flex-col gap-3.5">
        <div className="min-w-0">
          <FieldLabel htmlFor="edit-qty" required>{ORDER.quantity}</FieldLabel>
          <QtyField id="edit-qty" value={qty} onChange={setQty} unit={line.unit ? unitWord(amount, line.unit) : ''} />
        </div>
        <div className="min-w-0">
          <FieldLabel required>{MATERIALS.head.comesFrom}</FieldLabel>
          <SourceChips value={source} onChange={setSource} />
        </div>
        {source !== 'yard' && catalog && (
          <div className="min-w-0">
            <FieldLabel required>{ORDER.vendor}</FieldLabel>
            <VendorList vendors={catalog.vendors} value={vendorId} onChange={setVendorId} />
          </div>
        )}
        <Problem error={error} />
      </div>
    </VisitPopup>
  )
}

// ---------------------------------------------------------------------------
// ⋯ Spread over days
// ---------------------------------------------------------------------------

export function SpreadSheet({ read, line, phone, onClose, onDone }: SheetProps & { line: MaterialLine }) {
  const days = read.overview.days.days.map((tile) => tile.day)
  const [typed, setTyped] = useState<Record<number, string> | null>(null)
  const { busy, error, submit } = useSubmit(read, onDone, onClose)

  useEffect(() => {
    let live = true
    request<MaterialLineDetail>(visitPath(read.id, `/materials/${encodeURIComponent(line.id)}`))
      .then((detail) => {
        if (!live) return
        const start: Record<number, string> = {}
        for (const row of detail.days) start[row.day.index] = String(row.qty)
        setTyped(start)
      })
      .catch(() => { if (live) setTyped({}) })
    return () => { live = false }
  }, [read.id, line.id])

  const rows = days
    .map((dayRef) => ({ day: dayRef.index, qty: Number(typed?.[dayRef.index] ?? '') }))
    .filter((row) => row.qty > 0)

  return (
    <VisitPopup
      width={470}
      phone={phone}
      title={MATERIALS.menu.spread}
      sub={line.names.name}
      mark={lineTile}
      onClose={onClose}
      footer={(
        <>
          <VisitButton size={phone ? 'phone' : 'popup'} onClick={onClose}>{COMMON.cancel}</VisitButton>
          <VisitButton
            size={phone ? 'phone' : 'popup'}
            tone="primary"
            glyph="check"
            disabled={busy || typed === null || rows.length === 0}
            onClick={() => void submit('PATCH', `/materials/${encodeURIComponent(line.id)}`, { version: line.version, days: rows })}
          >
            {COMMON.save}
          </VisitButton>
        </>
      )}
    >
      <div className="flex flex-col gap-2.5">
        <FieldLabel>{COMMON.needed}</FieldLabel>
        {days.map((dayRef) => (
          <div key={dayRef.index} className="grid grid-cols-[1fr_160px] items-center gap-3">
            <label htmlFor={`spread-${dayRef.index}`} className="text-[13px] font-semibold">
              {COMMON.dayDate(String(dayRef.index), dayText(dayRef.date, 'date'))}
            </label>
            <QtyField
              id={`spread-${dayRef.index}`}
              value={typed?.[dayRef.index] ?? ''}
              onChange={(value) => setTyped((was) => ({ ...(was ?? {}), [dayRef.index]: value }))}
              unit={line.unit ? line.unit.many : ''}
            />
          </div>
        ))}
        <Problem error={error} />
      </div>
    </VisitPopup>
  )
}

// ---------------------------------------------------------------------------
// ⋯ Move to another visit (on the same job)
// ---------------------------------------------------------------------------

export function MoveLineSheet({ read, line, phone, onClose, onDone }: SheetProps & { line: MaterialLine }) {
  const targets = read.rail.jobVisits.rows.filter((row) => !row.thisVisit && !row.cancelled && !row.closed)
  const [to, setTo] = useState<string | null>(targets[0]?.id ?? null)
  const [why, setWhy] = useState('')
  const { busy, error, submit } = useSubmit(read, onDone, onClose)

  return (
    <VisitPopup
      width={470}
      phone={phone}
      title={MATERIALS.menu.move}
      sub={line.names.name}
      mark={lineTile}
      onClose={onClose}
      footer={(
        <>
          <VisitButton size={phone ? 'phone' : 'popup'} onClick={onClose}>{COMMON.cancel}</VisitButton>
          <VisitButton
            size={phone ? 'phone' : 'popup'}
            tone="primary"
            disabled={busy || !to}
            onClick={() => void submit('POST', `/materials/${encodeURIComponent(line.id)}/move`, { toVisitId: to, reason: why.trim() })}
          >
            {MATERIALS.menu.move}
          </VisitButton>
        </>
      )}
    >
      <div className="flex flex-col gap-3.5">
        <div role="radiogroup" aria-label={MATERIALS.menu.move} className="overflow-hidden rounded-[10px] border border-line">
          {targets.map((row) => {
            const on = row.id === to
            return (
              <button
                key={row.id}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => setTo(row.id)}
                className={cn('flex min-h-[52px] w-full items-center gap-2.5 border-t border-line-faint px-3 py-2 text-left first:border-t-0',
                  on ? 'bg-green-band-bg' : 'bg-surface hover:bg-hover')}
              >
                <RadioDot on={on} size={16} />
                <VisitMark mark={row.mark} size={28} />
                <span className="flex min-w-0 flex-col">
                  <b className="truncate text-[13px] font-semibold">{row.name}</b>
                  <span className="text-[12px] text-text-muted">{row.reference}</span>
                </span>
              </button>
            )
          })}
        </div>
        <div className="min-w-0">
          <FieldLabel htmlFor="move-why">{COMMON.why}</FieldLabel>
          <textarea
            id="move-why"
            value={why}
            onChange={(event) => setWhy(event.target.value)}
            className="block h-14 w-full resize-none rounded-button border border-line bg-surface px-3 py-2.5 text-[13px] leading-normal text-text"
          />
        </div>
        <Problem error={error} />
      </div>
    </VisitPopup>
  )
}

// ---------------------------------------------------------------------------
// ⋯ Remove — voided with who, when and why (rule 2)
// ---------------------------------------------------------------------------

export function RemoveLineSheet({ read, line, phone, onClose, onDone }: SheetProps & { line: MaterialLine }) {
  const [why, setWhy] = useState('')
  const { busy, error, submit } = useSubmit(read, onDone, onClose)
  return (
    <VisitPopup
      width={470}
      phone={phone}
      title={MATERIALS.menu.remove}
      sub={line.names.name}
      mark={<VisitTile glyph="close" tone="danger" size={34} />}
      onClose={onClose}
      footer={(
        <>
          <VisitButton size={phone ? 'phone' : 'popup'} onClick={onClose}>{COMMON.cancel}</VisitButton>
          <VisitButton
            size={phone ? 'phone' : 'popup'}
            tone="danger"
            disabled={busy || !why.trim()}
            onClick={() => void submit('POST', `/materials/${encodeURIComponent(line.id)}/remove`, { reason: why.trim() })}
          >
            {MATERIALS.menu.remove}
          </VisitButton>
        </>
      )}
    >
      <div className="flex flex-col gap-3">
        <div className="min-w-0">
          <FieldLabel htmlFor="remove-why" required>{COMMON.why}</FieldLabel>
          <textarea
            id="remove-why"
            autoFocus
            value={why}
            onChange={(event) => setWhy(event.target.value)}
            className="block h-14 w-full resize-none rounded-button border border-line bg-surface px-3 py-2.5 text-[13px] leading-normal text-text"
          />
        </div>
        <Problem error={error} />
      </div>
    </VisitPopup>
  )
}

